/**
 * Chat8DebugModal - Modal for WebSocket debug tools
 * Only available in development environment
 */

import React, { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Terminal } from "lucide-react";
import { Chat8DebugPanelContent } from "./Chat8DebugPanelContent";

interface Chat8DebugModalProps {
  triggerButton?: React.ReactNode;
}

/**
 * Chat8DebugModal - Modal to display WebSocket debug tools
 * Modal wrapper that contains Chat8DebugPanelContent
 */
export const Chat8DebugModal: React.FC<Chat8DebugModalProps> = React.memo(({
  triggerButton
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Do not render in production
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  // Default button if not provided
  const defaultTriggerButton = useMemo(() => (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2 bg-navy-700 border-navy text-platinum hover:bg-navy-600"
    >
      <Terminal className="h-4 w-4" />
      <span>Debug</span>
    </Button>
  ), []);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || defaultTriggerButton}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col bg-navy-800 border-navy z-modal">
        <DialogHeader className="flex-shrink-0 pb-4 border-b border-navy bg-navy-700/50">
          <DialogTitle className="text-platinum font-bold flex items-center gap-2">
            <Terminal className="w-5 h-5 text-amber-400" />
            🔧 Debug WebSocket
          </DialogTitle>
          <p className="text-sm text-silver/70 mt-1">
            Ferramentas de desenvolvimento para debug e teste de WebSocket
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <Chat8DebugPanelContent />
        </div>

        <div className="flex-shrink-0 border-t border-navy bg-navy-700/30 px-6 py-3">
          <div className="text-xs text-silver/60 space-y-1">
            <div>💡 <span className="font-semibold">Console:</span> WebSocketReconnectionTests.runAllTests()</div>
            <div>💡 <span className="font-semibold">Ver logs:</span> Abra o console do navegador (F12)</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

Chat8DebugModal.displayName = 'Chat8DebugModal';
